/**
 * Button variant styles (class-variance-authority)
 * @layer atom
 * @package @plerity/ui-core
 *
 * Extracted to a leaf module so Button.tsx and Button.types.ts can both import
 * `buttonVariants` without forming a Button.tsx <-> Button.types.ts import
 * cycle (import-cycles-packages baseline, 2026-09-15). Re-exported from
 * Button.tsx so the public `@plerity/ui-core` import path is unchanged.
 */

import { cva } from "class-variance-authority";

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary: "bg-brand text-brand-fg hover:bg-brand-hover focus-visible:ring-brand-focus",
        // secondary/ghost/outline: literal gray ramp — do NOT use `bg-surface-subtle`,
        // `text-[var(--color-text-primary)]`, or `border-[var(--color-border-default)]`.
        // Those emit `var(--color-*)` with NO fallback and are stripped from the React
        // SPA apps' Tailwind v4 build. The literal ramp keeps fallbacks so dark mode
        // renders true-dark and survives the strip.
        secondary:
          "bg-gray-100 text-gray-900 border border-gray-200 hover:bg-gray-200 focus-visible:ring-gray-400 dark:focus-visible:ring-gray-500 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-600",
        ghost:
          "bg-transparent hover:bg-gray-100 hover:text-gray-900 focus-visible:ring-gray-400 dark:focus-visible:ring-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-100",
        // No `dark:bg-red-500`: white on red-500 is 3.76:1 (AA FAIL, Ladle a11y gate 2026-10-18).
        // red-600 keeps white text >= 4.5:1 in both themes. The explicit `dark:bg-red-600`
        // pair (same AA-safe shade) satisfies every app's light-bg→dark-pair check, which
        // otherwise flagged each variant="danger" call site (social TrendRadar, 2026-10-19).
        danger:
          "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600 dark:bg-red-600 dark:hover:bg-red-700 dark:focus-visible:ring-red-500",
        outline:
          "border border-gray-200 bg-transparent hover:bg-gray-100 focus-visible:ring-gray-400 dark:focus-visible:ring-gray-500 dark:border-gray-700 dark:hover:bg-gray-700",
      },
      size: {
        sm: "h-[var(--app-btn-height-sm,2rem)] px-[var(--app-btn-padding-x-sm,0.75rem)] text-[length:var(--app-btn-font-size-sm,0.875rem)] rounded-[var(--app-btn-radius-sm,6px)]",
        md: "h-[var(--app-btn-height-md,2.5rem)] px-[var(--app-btn-padding-x-md,1rem)] text-[length:var(--app-btn-font-size-md,0.875rem)] rounded-[var(--app-btn-radius-md,8px)]",
        lg: "h-[var(--app-btn-height-lg,3rem)] px-[var(--app-btn-padding-x-lg,1.5rem)] text-[length:var(--app-btn-font-size-lg,1rem)] rounded-[var(--app-btn-radius-lg,10px)]",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  }
);
